import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { orderService } from "../services/order";
import { productService } from "../services/product"; // To select products for order items
import AutocompleteClient from "../components/AutocompleteClient";
import { formatDateForPortugal } from "../utils/dateUtils";
import { extractPackMultiplier, getUnitWeight } from "../utils/packUtils";

const CreateOrder = () => {
  const [orderData, setOrderData] = useState({
    order_number: "",
    client_name: "",
    client_nif: "",
    order_date: new Date().toISOString().split('T')[0], // Data do pedido (padrão: hoje)
    delivery_date: "",
    total_cheese_count: 0, // Será calculado automaticamente
    total_weight_kg: 0, // Será calculado automaticamente
    delivery_status: "pending",
    destination_address: "",
    recipient_name: "",
    packing_details: "",
    is_weekly: false,
    items: [],
  });

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    // Recalcular total_cheese_count e total_weight_kg sempre que os itens mudarem
    let newTotalCheeseCount = 0;
    let newTotalWeightKg = 0;

    orderData.items.forEach((item) => {
      const product = products.find((p) => p.id === item.product_id);

      // Extrair multiplicador de pack do nome do produto
      const packMultiplier = product ? extractPackMultiplier(product.name) : 1;

      // Calcular unidades reais considerando o multiplicador
      const realUnits = item.ordered_quantity * packMultiplier;
      newTotalCheeseCount += realUnits;

      if (product) {
        // Peso total = unidades reais × peso unitário
        newTotalWeightKg += realUnits * getUnitWeight(product);
      }
    });

    setOrderData((prev) => ({
      ...prev,
      total_cheese_count: newTotalCheeseCount,
      total_weight_kg: newTotalWeightKg,
    }));
  }, [orderData.items, products]); // Depende de items e products para recalcular

  const fetchProducts = async () => {
    try {
      const data = await productService.getProducts();
      setProducts(data);
    } catch (err) {
      setError(err.message || "Erro ao carregar produtos");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    if (client) {
      setOrderData((prev) => ({
        ...prev,
        client_name: client.nome,
        client_nif: client.nif || "",
        destination_address: client.zona || "",
        recipient_name: client.nome || "",
        packing_details: client.packaging || "",
      }));

      // Carregar produtos específicos do cliente
      try {
        const result = await productService.getProductsByClientNif(client.nif);
        setProducts(result.products || []);
      } catch (err) {
        console.error("Erro ao carregar produtos do cliente:", err);
        // Fallback para todos os produtos se houver erro
        fetchProducts();
      }
    } else {
      // Clear client data if no client selected
      setOrderData((prev) => ({
        ...prev,
        client_name: "",
        client_nif: "",
        destination_address: "",
        recipient_name: "",
        packing_details: "",
      }));
      // Recarregar todos os produtos
      fetchProducts();
    }
  };

  const addOrderItem = () => {
    if (!selectedProduct || selectedQuantity <= 0) {
      setError("Selecione um produto e insira uma quantidade válida");
      return;
    }

    const product = products.find((p) => p.id === selectedProduct);

    setOrderData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: selectedProduct,
          product_name: product.name, // For display only
          ordered_quantity: selectedQuantity,
        },
      ],
    }));

    // Clear selection
    setSelectedProduct("");
    setSelectedQuantity(0);
  };

  const removeOrderItem = (index) => {
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");

    // Em desenvolvimento, permite criar encomenda sem cliente cadastrado
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (!isDevelopment && !selectedClient) {
      setError("Selecione um cliente");
      return;
    }

    // Validar que pelo menos o nome e NIF estão preenchidos (mesmo que manualmente)
    if (!orderData.client_name || !orderData.client_nif) {
      setError("Nome do cliente e NIF são obrigatórios");
      return;
    }

    if (orderData.items.length === 0) {
      setError("Adicione pelo menos um item à encomenda");
      return;
    }

    setSubmitting(true);
    try {
      // Corrigir as datas para o timezone de Portugal antes de enviar
      const orderDataWithCorrectDate = {
        ...orderData,
        origin_source: "manual",
        order_date: formatDateForPortugal(orderData.order_date),
        delivery_date: formatDateForPortugal(orderData.delivery_date)
      };
      
      const result = await orderService.createOrder(orderDataWithCorrectDate);
      navigate("/orders", { state: { emailConfirmation: result.emailConfirmation } });
    } catch (err) {
      setError(err.message || "Erro ao criar encomenda");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <h1 className="text-3xl font-bold text-neutral-800 mb-8">
        Criar Nova Encomenda
      </h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-md rounded-lg p-8"
      >
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Número da Encomenda (opcional - gerado automaticamente se vazio)
            </label>
            <input
              type="text"
              name="order_number"
              value={orderData.order_number}
              onChange={handleInputChange}
              placeholder="Deixe vazio para gerar automaticamente"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Cliente *
            </label>
            <AutocompleteClient
              onClientSelect={handleClientSelect}
              selectedClient={selectedClient}
              placeholder="Digite o nome do cliente ou NIF..."
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              NIF do Cliente
            </label>
            <input
              type="text"
              name="client_nif"
              value={orderData.client_nif}
              onChange={handleInputChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Data do Pedido
            </label>
            <input
              type="date"
              name="order_date"
              value={orderData.order_date}
              onChange={handleInputChange}
              required
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Data de Entrega
            </label>
            <input
              type="date"
              name="delivery_date"
              value={orderData.delivery_date}
              onChange={handleInputChange}
              required
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
            />
          </div>

          <div className="col-span-1 sm:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={orderData.is_weekly}
                onChange={(e) =>
                  setOrderData((prev) => ({ ...prev, is_weekly: e.target.checked }))
                }
                className="w-4 h-4 accent-orange-500"
              />
              <span className="text-xs sm:text-sm text-neutral-700 font-medium">
                É semanal?
                <span className="ml-1 text-neutral-400 font-normal">
                  (a encomenda será repetida automaticamente todas as semanas)
                </span>
              </span>
            </label>
          </div>

          {/* Campos total_cheese_count e total_weight_kg serão calculados automaticamente e não precisam de input */}
          <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                Quantidade Total de Queijos
              </label>
              <p className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md bg-neutral-100 text-neutral-700 text-xs sm:text-sm">
                {orderData.total_cheese_count}
              </p>
            </div>
            <div>
              <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                Peso Total (kg)
              </label>
              <p className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md bg-neutral-100 text-neutral-700 text-xs sm:text-sm">
                {(orderData.total_weight_kg || 0).toFixed(2)} kg
              </p>
            </div>
          </div>

          <div className="col-span-1 sm:col-span-2">
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Endereço de Destino
            </label>
            <textarea
              name="destination_address"
              value={orderData.destination_address}
              onChange={handleInputChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Nome do Recebedor
            </label>
            <input
              type="text"
              name="recipient_name"
              value={orderData.recipient_name}
              onChange={handleInputChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Detalhes da Embalagem
            </label>
            <textarea
              name="packing_details"
              value={orderData.packing_details}
              onChange={handleInputChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm"
              rows="3"
            />
          </div>

          <div className="col-span-1 sm:col-span-2">
            <h2 className="text-lg sm:text-xl font-semibold text-neutral-800 mb-3 sm:mb-4">
              Itens da Encomenda
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Produto *
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
                >
                  <option value="">Selecione um produto</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} {product.is_variant ? "[Variante]" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
                  Quantidade
                </label>
                <input
                  type="number"
                  value={selectedQuantity}
                  onChange={(e) =>
                    setSelectedQuantity(Number(e.target.value))
                  }
                  min="0"
                  step="0.01"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={addOrderItem}
                  className="w-full bg-neutral-900 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-800 text-xs sm:text-sm"
                >
                  Adicionar Item
                </button>
              </div>
            </div>

            {orderData.items.length > 0 && (
              <div className="bg-neutral-100 rounded-md p-3 sm:p-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs sm:text-sm">
                        <th className="pb-2">Produto</th>
                        <th className="pb-2">Quantidade</th>
                        <th className="pb-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderData.items.map((item, index) => (
                        <tr key={index} className="border-t border-neutral-200 text-xs sm:text-sm">
                          <td className="py-2">{item.product_name}</td>
                          <td className="py-2">{item.ordered_quantity}</td>
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeOrderItem(index)}
                              className="text-red-600 hover:text-red-800 text-xs sm:text-sm"
                            >
                              Remover
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 sm:mt-8 flex flex-col-reverse sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            type="button"
            onClick={() => navigate("/orders")}
            className="w-full sm:w-auto bg-neutral-100 text-neutral-900 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-200 text-xs sm:text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto bg-neutral-900 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-800 text-xs sm:text-sm"
          >
            {submitting ? 'Criando encomenda…' : 'Criar Encomenda'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateOrder;
